import type React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3 font-semibold',
  {
    variants: {
      size: {
        sm: 'px-2 py-0 text-xs rounded-sm',
        md: 'px-2.5 py-0.5 text-sm',
      },
      colorScheme: {
        primary: 'text-primary',
        secondary: 'text-secondary-foreground',
        success: 'text-green-600',
        warning: 'text-amber-700',
        error: 'text-destructive',
      },
      variant: {
        solid: '',
        outline: 'bg-transparent',
      },
      withDot: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { colorScheme: 'primary', variant: 'solid', class: 'bg-primary/5 border-primary/20' },
      { colorScheme: 'secondary', variant: 'solid', class: 'bg-secondary/5 border-secondary' },
      { colorScheme: 'success', variant: 'solid', class: 'bg-green-500/5 border-green-500/20' },
      { colorScheme: 'warning', variant: 'solid', class: 'bg-amber-500/5 border-amber-500/20' },
      { colorScheme: 'error', variant: 'solid', class: 'bg-red-500/5 border-red-500/20' },

      { colorScheme: 'primary', variant: 'outline', class: 'border-primary/40' },
      { colorScheme: 'secondary', variant: 'outline', class: 'border-secondary' },
      { colorScheme: 'success', variant: 'outline', class: 'border-green-500/40' },
      { colorScheme: 'warning', variant: 'outline', class: 'border-amber-500/40' },
      { colorScheme: 'error', variant: 'outline', class: 'border-red-500/40' },
    ],
    defaultVariants: {
      size: 'md',
      colorScheme: 'primary',
      variant: 'solid',
      withDot: false,
    },
  },
);

type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
  };

function Dot({ className }: { className?: string }) {
  return (
    <span className={cn('mr-1 inline-block size-1.5 rounded-full', className)} aria-hidden="true" />
  );
}

function colorDotClass(colorScheme: NonNullable<BadgeProps['colorScheme']>): string {
  switch (colorScheme) {
    case 'primary': {
      return 'bg-primary';
    }
    case 'secondary': {
      return 'bg-secondary-foreground';
    }
    case 'success': {
      return 'bg-green-500';
    }
    case 'warning': {
      return 'bg-amber-500';
    }
    case 'error': {
      return 'bg-red-500';
    }
    default: {
      return 'bg-primary';
    }
  }
}

function Badge({
  className,
  size,
  colorScheme,
  variant,
  withDot,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ size, colorScheme, variant, withDot }), className)}
      {...props}
    >
      {withDot ? <Dot className={colorDotClass(colorScheme ?? 'primary')} /> : null}
      {props.children}
    </Comp>
  );
}

export { Badge, badgeVariants };
